import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/system';
import TextInput from '../../TextFields/TextInput.js';
import DateInput from '../../TextFields/DateInput.js';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
import NumberInput from '../../../components/TextFields/NumberInput.js';
import PhoneInput from '../../../components/TextFields/PhoneInput.js';
import * as Services from '../../../services/index.js';
import { parseInitialUserData, returnRegularInfoChanges, returnPersonalInfoChanges, returnEmploymentInfoChanges, initialErrorState, parseUserDataForAddUpdate } from './parseEmployeeData.js';
import BubbleSelectBlock from '../../BubbleSelectBlock.js';
import validateForm from './validateForm.js';
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  row: {
    display: 'flex',
    flexDirection: 'row'
  },
  divider: {
    width: 40
  }
};

/**
 * 
 * @returns 
 */
const index = ({
  open,
  isEdit,
  close,
  currentEmployee,
  classData,
  divisions,
  goBack,
  currentAccountMeta,
  isAdmin = false,
  updateUser,
  addUser,
  loader
}) => {
  const [user, setUser] = useState(parseInitialUserData(currentEmployee, isEdit, classData, divisions));
  const [errorState, setErrorState] = useState(initialErrorState);
  const theme = useTheme();
  const {
    firstname,
    lastname,
    email,
    birthdate,
    className,
    divisionName,
    hireDate,
    gender,
    title,
    coverageType,
    province,
    earningsType,
    salary,
    hourlyWage,
    hoursPerWeek,
    language,
    payrollID,
    waiveEligibilityPeriod,
    phone,
    maritlStat
  } = user;
  const {
    firstnameError,
    lastnameError,
    emailError,
    birthdateError,
    classNameError,
    divisionNameError,
    hireDateError,
    genderError,
    titleError,
    coverageTypeError,
    provinceError,
    earningsTypeError,
    salaryError,
    hourlyWageError,
    hoursPerWeekError,
    waiveEligibilityPeriodError,
    employeeDetailsPersonalDetailsError,
    phoneError,
    maritlStatError
  } = errorState;
  useEffect(() => {
    setUser(parseInitialUserData(currentEmployee, isEdit, classData, divisions));
  }, [currentEmployee, isEdit]);

  /**
   * Handles input change and updates user state
   * 
   * @param {string} field - Field name to update
   * @param {string|number} value - New value for the field
   */
  const _handleInputChange = (field, value) => {
    setUser(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Validates the form and sets error state if validation fails
   * 
   * @returns {Promise<boolean>} True if the form is valid, false otherwise
   */
  const _validateFormAndSetErrorState = async () => {
    const formErrors = await validateForm(user, currentEmployee, isEdit, isAdmin, classData);
    if (!formErrors) {
      return true;
    }
    setErrorState({
      ...formErrors
    });
    return false;
  };

  /**
   * Resets the form to its initial state
   */
  const _resetForm = () => {
    setErrorState(initialErrorState);
    parseInitialUserData(currentEmployee, false, classData, divisions);
  };

  /**
   * Resets the form to its initial state
   */
  const _onClose = () => {
    _resetForm();
    close();
  };

  /**
   * Saves the form data, conditionally with a change request if used in the control site, and with a regular update in the master site
   * 
   * @returns {Promise<boolean>} True if the save was successful, false otherwise
   */
  const _save = async () => {
    try {
      const isFormValid = await _validateFormAndSetErrorState();
      if (!isFormValid) {
        return false;
      }
      setErrorState(initialErrorState);
      if (isAdmin && isEdit) {
        const regularUpdateData = returnRegularInfoChanges(user, currentEmployee, divisions);
        const personalInfoChanges = returnPersonalInfoChanges(user, currentEmployee);
        const employmentDetailsChanges = returnEmploymentInfoChanges(user, currentEmployee, classData);
        updateUser(regularUpdateData, personalInfoChanges, employmentDetailsChanges);
        return true;
      }
      const dataForUpdate = parseUserDataForAddUpdate(user, classData, divisions, isAdmin);

      // methods for master site to be added here
      const success = isEdit ? updateUser(dataForUpdate) : await addUser(dataForUpdate);
      if (success) {
        _onClose();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    title: isEdit ? i18n.t(tokens.labels.changeRequests.editMemberDetails) : i18n.t(tokens.labels.employees.newMember),
    medium: true,
    primaryButton: {
      label: i18n.t(tokens.buttons.save),
      onClick: _save
    },
    goBack: goBack,
    onClose: _onClose,
    loader: loader
  }, employeeDetailsPersonalDetailsError ? /*#__PURE__*/React.createElement(Text, {
    large: true,
    light: true,
    customStyle: {
      color: theme.palette.error.main
    }
  }, i18n.t(tokens.labels.validations.editEmployeeDetailsValidationError)) : null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true
  }, i18n.t(tokens.labels.changeRequests.personalDetails)), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: firstname,
    onChange: e => _handleInputChange('firstname', e.target.value),
    error: Boolean(firstnameError),
    helperText: firstnameError,
    fullWidth: true,
    required: true,
    autoFocus: !isEdit
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: lastname,
    onChange: e => _handleInputChange('lastname', e.target.value),
    error: Boolean(lastnameError),
    helperText: lastnameError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.email),
    value: email,
    onChange: e => _handleInputChange('email', e.target.value),
    error: Boolean(emailError),
    helperText: emailError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD),
    value: birthdate,
    onChange: e => _handleInputChange('birthdate', e.target.value),
    error: Boolean(birthdateError),
    helperText: birthdateError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.gender),
    value: gender,
    onChange: e => _handleInputChange('gender', e.target.value),
    error: Boolean(genderError),
    helperText: genderError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.genderOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.provinceOfResidence),
    value: province,
    onChange: e => _handleInputChange('province', e.target.value),
    error: Boolean(provinceError),
    helperText: provinceError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.provinceOptions()
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.individualDependents),
    value: coverageType,
    onChange: e => _handleInputChange('coverageType', e.target.value),
    error: Boolean(coverageTypeError),
    helperText: coverageTypeError,
    fullWidth: true,
    required: true,
    select: true,
    disabled: Boolean(isEdit && currentEmployee?.cert),
    selectOptions: Services.selectFieldOptions.coverageOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.employeeLanguage),
    value: language,
    onChange: e => _handleInputChange('language', e.target.value),
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.languageOptions()
  })), isEdit && isAdmin ? /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(PhoneInput, {
    label: i18n.t(tokens.labels.phoneNumber),
    value: phone,
    onChange: e => _handleInputChange('phone', e.target.value),
    error: Boolean(phoneError),
    helperText: phoneError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.maritalStatus),
    fullWidth: true,
    required: true,
    value: maritlStat,
    onChange: e => _handleInputChange('maritlStat', e.target.value),
    select: true,
    selectOptions: Services.selectFieldOptions.maritalStatusOptions(),
    error: Boolean(maritlStatError),
    helperText: maritlStatError
  })) : null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true,
    customStyle: {
      marginTop: 10
    }
  }, i18n.t(tokens.labels.employees.employmentDetails)), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.class),
    value: className,
    onChange: e => _handleInputChange('className', e.target.value),
    error: Boolean(classNameError),
    helperText: classNameError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Object.values(classData).map(item => item.title)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.division),
    value: divisionName,
    onChange: e => _handleInputChange('divisionName', e.target.value),
    error: Boolean(divisionNameError),
    helperText: divisionNameError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Object.values(divisions).map(item => item.title)
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.employees.hireDate),
    value: hireDate,
    onChange: e => _handleInputChange('hireDate', e.target.value),
    error: Boolean(hireDateError),
    helperText: hireDateError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.occupation),
    value: title,
    onChange: e => _handleInputChange('title', e.target.value),
    error: Boolean(titleError),
    helperText: titleError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.earningsType),
    value: earningsType,
    onChange: e => _handleInputChange('earningsType', e.target.value),
    error: Boolean(earningsTypeError),
    helperText: earningsTypeError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.earningsTypeOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), localizationMaps.toEnglish("earningsTypes", earningsType) === 'Hourly' ? /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.hourlyRate),
    prefix: "$ ",
    value: hourlyWage,
    onChange: e => _handleInputChange('hourlyWage', e.target.value),
    error: Boolean(hourlyWageError),
    helperText: hourlyWageError,
    fullWidth: true,
    required: true
  }) : /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.salary),
    prefix: "$ ",
    value: salary,
    onChange: e => _handleInputChange('salary', e.target.value),
    error: Boolean(salaryError),
    helperText: salaryError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.hoursPerWeek),
    value: hoursPerWeek,
    onChange: e => _handleInputChange('hoursPerWeek', e.target.value),
    error: Boolean(hoursPerWeekError),
    helperText: hoursPerWeekError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.payrollID),
    value: payrollID,
    onChange: e => _handleInputChange('payrollID', e.target.value),
    fullWidth: true
  })),
  // we only allow this when the account is live, if it's pending or in revisions then it's census additions and should be waived automatically by the backend systems
  currentAccountMeta?.status === 1 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true,
    display: "block",
    align: "left",
    margin: "8px 0 4px"
  }, i18n.t(tokens.labels.employees.youMustChooseToWaive)), /*#__PURE__*/React.createElement(Text, {
    medium: true,
    display: "block",
    align: "left",
    margin: "4px 0 8px"
  }, i18n.t(tokens.labels.employees.waivingMeansThat)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    customStyle: {
      marginTop: "8px",
      marginLeft: "8px"
    },
    allowToggleOff: true,
    first: {
      title: i18n.t(tokens.buttons.doNotWaive),
      selected: !waiveEligibilityPeriod,
      field: 'doNotWaive',
      customStyle: {
        fontSize: 12
      }
    },
    second: {
      title: i18n.t(tokens.buttons.waive),
      selected: waiveEligibilityPeriod,
      field: 'waive',
      customStyle: {
        fontSize: 12
      }
    },
    handleChange: field => {
      if (field === 'doNotWaive') {
        _handleInputChange('waiveEligibilityPeriod', false);
      } else if (field === 'waive') {
        _handleInputChange('waiveEligibilityPeriod', true);
      }
    }
  }), waiveEligibilityPeriodError && /*#__PURE__*/React.createElement(Text, {
    medium: true,
    display: "block",
    align: "left",
    margin: "4px 0",
    color: "red"
  }, i18n.t(tokens.labels.employees.chooseToWaiveError))), /*#__PURE__*/React.createElement("span", {
    id: "census-form"
  }));
};
export default index;